import React, { useState, useMemo, useRef, useEffect } from 'react'
import cx from 'classnames'

import { MedicalPricingProps } from './interfaces/MedicalPricing'

const LOCATIONS = [
  { value: 'los-angeles', label: 'Los Angeles' },
  { value: 'new-york', label: 'New York' },
  { value: 'arizona', label: 'Arizona' },
  { value: 'san-francisco', label: 'San Francisco' },
  { value: 'virtual-care', label: 'Virtual Care' },
]

const PricingDropdown = ({
  label,
  options,
  activeValue,
  onChange,
}: {
  label?: string
  options: { value: string; label: string }[]
  activeValue: string | null
  onChange: (value: string) => void
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const activeLabel = options.find(o => o.value === activeValue)?.label || ''

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={ref} className="medical-pricing__location-dropdown">
      {label && <span className="medical-pricing__dropdown-label">{label}</span>}
      <button
        className="medical-pricing__location-dropdown__btn"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>{activeLabel}</span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          className={cx('medical-pricing__location-dropdown__chevron', {
            'medical-pricing__location-dropdown__chevron--open': open,
          })}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="medical-pricing__location-dropdown__list" role="listbox">
          {options.map(opt => (
            <li key={opt.value}>
              <button
                role="option"
                aria-selected={opt.value === activeValue}
                className={cx('medical-pricing__location-dropdown__item', {
                  'medical-pricing__location-dropdown__item--active': opt.value === activeValue,
                })}
                onClick={() => { onChange(opt.value); setOpen(false) }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export const MedicalPricing = ({ data }: { data: MedicalPricingProps }) => {
  const { title, disclosure, locations, backgroundColor, deepLinkId } = data
  
  // Get available locations from data (only those with content)
  const availableLocations = useMemo(() => {
    if (!locations) return []
    return locations.map(loc => {
      const locationData = LOCATIONS.find(l => l.value === loc.location)
      return {
        value: loc.location,
        label: locationData?.label || loc.location,
      }
    })
  }, [locations])

  // Auto-detect: only show filters if there are multiple locations
  const showFilters = availableLocations.length > 1
  const [activeFilter, setActiveFilter] = useState<string | null>(
    showFilters && availableLocations[0] ? availableLocations[0].value : null
  )

  const filteredLocations = useMemo(() => {
    // If only one location, show it without filtering
    if (!showFilters) return locations
    // If multiple locations and a filter is active, filter by it
    if (activeFilter) return locations?.filter(loc => loc.location === activeFilter)
    // Otherwise show all
    return locations
  }, [locations, activeFilter, showFilters])

  return (
    <div
      id={deepLinkId}
      className={cx('rel pys', backgroundColor, {
        cw: backgroundColor === 'bcb',
      })}
    >
      <div className="container--l mmb20 mxa outer p1">
        {title && <h2 className="module-title-serif mt0 pt0">{title}</h2>}
        {disclosure && (
          <p className="medical-pricing__disclosure">{disclosure}</p>
        )}
        
        {/* Mobile controls: location dropdown — hidden on desktop */}
        {showFilters && (
          <div className="medical-pricing__mobile-controls">
            <PricingDropdown
              label="Location"
              options={availableLocations}
              activeValue={activeFilter}
              onChange={setActiveFilter}
            />
          </div>
        )}

        {/* Column headers — desktop full table */}
        <div className="medical-pricing__col-headers medical-pricing__col-headers--desktop">
          {showFilters ? (
            <PricingDropdown
              options={availableLocations}
              activeValue={activeFilter}
              onChange={setActiveFilter}
            />
          ) : <div />}
          <div className="medical-pricing__col-header">Cash Rate</div>
          <div className="medical-pricing__col-header">
            Average CoPay Amount<br />
            <span className="medical-pricing__col-header-sub">(industry ave)</span>
          </div>
          <div className="medical-pricing__col-header">
            Average Coinsurance Cost<br />
            <span className="medical-pricing__col-header-sub">(once deductibles met)</span>
          </div>
          <div className="medical-pricing__col-header">Average Deductible Costs</div>
        </div>

        <div className="mb1 pb0">
          {filteredLocations?.map(locationGroup => (
            locationGroup.categories?.map(category => (
              <details
                key={category._key}
                className="rel medical-pricing__category"
              >
                <summary className="ss20 sm21 m0 cursor medical-pricing__summary">
                  <span className="medical-pricing__summary-text">
                    {category.categoryName}
                  </span>
                  <svg
                    className="medical-pricing__summary-icon"
                    width="13"
                    height="8"
                    viewBox="0 0 13 8"
                    fill="none"
                  >
                    <path
                      d="M1 1L6.5 6.5L12 1"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </summary>
                <div className="medical-pricing__procedures">
                  {category.procedures?.map(procedure => (
                    <div key={procedure._key} className="medical-pricing__procedure">
                      {/* Mobile: stacked prices */}
                      <div className="medical-pricing__procedure-row--mobile">
                        {procedure.name && <div className="medical-pricing__procedure-name ss18">{procedure.name}</div>}
                        {procedure.examples && (
                          <div className="medical-pricing__procedure-examples ss14">{procedure.examples}</div>
                        )}
                        <div className="medical-pricing__procedure-prices-mobile">
                          <div className="medical-pricing__price-row-mobile">
                            <span className="medical-pricing__price-label-mobile">Cash Rate</span>
                            <span className="medical-pricing__procedure-col ss16">
                              {procedure.cashRate ? `$${procedure.cashRate}` : '—'}
                            </span>
                          </div>
                          {procedure.insuranceNote ? (
                            <div className="medical-pricing__price-row-mobile">
                              <span className="medical-pricing__price-label-mobile">With insurance</span>
                              <span className="medical-pricing__insurance-note-mobile ss16">
                                {procedure.insuranceNote}
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="medical-pricing__price-row-mobile">
                                <span className="medical-pricing__price-label-mobile">Average CoPay Amount</span>
                                <span className="medical-pricing__procedure-col ss16">
                                  {procedure.copayAmount ? `$${procedure.copayAmount}` : '—'}
                                </span>
                              </div>
                              <div className="medical-pricing__price-row-mobile">
                                <span className="medical-pricing__price-label-mobile">Average Coinsurance Cost</span>
                                <span className="medical-pricing__procedure-col ss16">
                                  {procedure.coinsuranceCost ? `$${procedure.coinsuranceCost}` : '—'}
                                </span>
                              </div>
                              <div className="medical-pricing__price-row-mobile">
                                <span className="medical-pricing__price-label-mobile">Average Deductible Costs</span>
                                <span className="medical-pricing__procedure-col ss16">
                                  {procedure.deductibleCost ? `$${procedure.deductibleCost}` : '—'}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Desktop: full row */}
                      <div className="medical-pricing__procedure-row medical-pricing__procedure-row--desktop">
                        <div className="medical-pricing__procedure-name ss18">
                          {procedure.name}
                          {procedure.examples && (
                            <div className="medical-pricing__procedure-examples ss14">{procedure.examples}</div>
                          )}
                        </div>
                        <div className="medical-pricing__procedure-col ss18">
                          {procedure.cashRate ? `$${procedure.cashRate}` : '—'}
                        </div>
                        {procedure.insuranceNote ? (
                          <div className="medical-pricing__insurance-note-desktop ss14">
                            {procedure.insuranceNote}
                          </div>
                        ) : (
                          <>
                            <div className="medical-pricing__procedure-col ss18">
                              {procedure.copayAmount ? `$${procedure.copayAmount}` : '—'}
                            </div>
                            <div className="medical-pricing__procedure-col ss18">
                              {procedure.coinsuranceCost ? `$${procedure.coinsuranceCost}` : '—'}
                            </div>
                            <div className="medical-pricing__procedure-col ss18">
                              {procedure.deductibleCost ? `$${procedure.deductibleCost}` : '—'}
                            </div>
                          </>
                        )}
                      </div>
                      {procedure.description && (
                        <div className="medical-pricing__procedure-description ss16">
                          {procedure.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            ))
          ))}
        </div>
      </div>
    </div>
  )
}

export default MedicalPricing
